import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { accountingApis, sellerAndPlatformApis } from "apis";
import { flagConstants } from "constants/index";
import { StoreContext, updateAuthorizedSellerIds, updateSellers, useSellerDispatch, useSellerState } from "contexts";
import { GetSellerListExtraEnum, IBranch, IRoute } from "models";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { IUserDetailsInfo } from "teko-oauth2";
import { browserHistory, commonUtils, iframeUtils, sellerUtils } from "utils";

const { getWindowDimensions } = commonUtils;
const { getAccessibleSellers, getAuthorizedSellerIds, hasAllPlatforms } = sellerUtils;

const useAppMenu = (items: IRoute[]) => {
  const { location } = browserHistory;
  let selectedKey = location.pathname;
  const selectedKeySplitArr = location.pathname.split("/");
  let i = 1;
  let newSelectedKey = "";

  const getParentKey = (key: string): IRoute | undefined => {
    const newParentKey = items.find((item) => item.children && item.children.includes(key));
    if (newParentKey) return newParentKey;
    else if (i < selectedKeySplitArr.length) {
      newSelectedKey += `/${selectedKeySplitArr[i++]}`;
      selectedKey = newSelectedKey;
      return getParentKey(selectedKey);
    }
  };

  const parentKey = getParentKey(selectedKey);
  const openKey = parentKey ? parentKey.path : "/";

  return { selectedKey, openKey };
};

const useWindowDimensions = () => {
  const [dimensions, setDimensions] = useState(getWindowDimensions);

  useEffect(() => {
    const handleResize = () => setDimensions(getWindowDimensions());
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return dimensions;
};

/** This hook inits the fetching of sellers data into SellerContext.
 * Use this hook on pages that need sellers data (e.g. for the <SellerSwitch />) */
const useSellerFetch = (currentUser: IUserDetailsInfo, filterSellerWithPlatform?: boolean) => {
  const { featureFlagsData } = useContext(StoreContext);
  const allowedToUseAPISellerList =
    featureFlagsData[flagConstants.EXTENDED_FLAG_KEYS.ALLOW_TO_USE_API_SELLER_LIST_HWF_2012.key]?.enabled;

  const sellerDispatch = useSellerDispatch();
  const sellerState = useSellerState();
  useEffect(() => {
    const fetchSellerList = async () => {
      const authorizedSellerIds = getAuthorizedSellerIds(currentUser);

      if (authorizedSellerIds.length && !sellerState.sellers) {
        const assignedPlatformId = currentUser.meta_data?.platformId?.toString() || undefined;
        if (allowedToUseAPISellerList) {
          const { currentPlatform } = iframeUtils.getDataFromIframe();
          const isAllSeller = authorizedSellerIds?.[0] === 0;
          updateAuthorizedSellerIds(sellerDispatch, authorizedSellerIds);

          // Use default pageSize (200). On Staff Admin, it always gets one seller.
          // Child app (standalone link) can fetch all sellers by platform.
          // Since it's DEV-only, safe to ignore if it exceeds the limit.
          const result = await sellerAndPlatformApis.getSellerList(
            {
              ids: isAllSeller ? undefined : authorizedSellerIds.join(","),
              platformIds: hasAllPlatforms(currentUser) && currentPlatform ? `${currentPlatform}` : assignedPlatformId,
              extra: GetSellerListExtraEnum.ExcludeFilterCreatedBySeller,
            },
            authorizedSellerIds?.[0].toString() || "0"
          );
          updateSellers(sellerDispatch, getAccessibleSellers(result.sellers, authorizedSellerIds));
        } else {
          const getSellerParams = () => {
            if (!filterSellerWithPlatform) return undefined;
            if (hasAllPlatforms(currentUser)) return undefined;
            if (!assignedPlatformId) return undefined;
            return {
              platformIds: assignedPlatformId,
            };
          };
          updateAuthorizedSellerIds(sellerDispatch, authorizedSellerIds);
          const result = await sellerAndPlatformApis.getSellersOrEmpty(getSellerParams());
          updateSellers(sellerDispatch, getAccessibleSellers(result, authorizedSellerIds));
        }
      }
    };
    fetchSellerList();
  }, [currentUser, sellerDispatch, sellerState.sellers]);
};

const useIsMountedRef = () => {
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  return isMountedRef;
};

const useBranchData = () => {
  const { currentSeller } = useSellerState();
  const sellerId = currentSeller?.id;
  const [branches, setBranches] = useState<IBranch[]>([]);
  useEffect(() => {
    const fetchBranchesBySeller = async () => {
      const newBranches = await accountingApis.getBranches(sellerId);
      setBranches(newBranches);
    };

    fetchBranchesBySeller();
  }, [sellerId]);

  return { branches };
};

/**
 *
 * @returns {URLSearchParams}
 */
export const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

type ItemKey = string | number;

/**
 * A hook that wraps an async function with automatic loading state management.
 * The loading state is automatically set to true when the function starts and false when it ends,
 * regardless of how the function exits (normal return, early return, or error).
 *
 * @returns A tuple of [wrappedFunction, isLoading, setIsLoading]
 *
 * @example
 * const [handleSubmit, isSubmitting, setIsSubmitting] = useWithLoading(async (data: FormData) => {
 *   const result = await api.submit(data);
 *   if (!result.success) return; // Loading state still handled
 *   // ... rest of logic
 * });
 *
 * // You can also manually control the loading state if needed
 * setIsSubmitting(true);
 */
const useWithLoading = <T extends (...args: any[]) => Promise<any>>(
  fn: T
): [T, boolean, React.Dispatch<React.SetStateAction<boolean>>] => {
  const [isLoading, setIsLoading] = useState(false);
  const fnRef = useRef(fn);

  // Keep the ref updated with the latest fn
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const wrappedFn = useCallback(async (...args: Parameters<T>) => {
    setIsLoading(true);
    try {
      return await fnRef.current(...args);
    } finally {
      setIsLoading(false);
    }
  }, []) as T;

  return [wrappedFn, isLoading, setIsLoading];
};

const useItemCheckbox = <T>(itemKey: ItemKey) => {
  type ItemMap = Map<string, { item: T; checked: boolean }>;

  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const [checkedMap, setCheckedMap] = useState<{ [key: string]: boolean }>({});
  const itemMap: ItemMap = useMemo(() => new Map(), []);

  const onCheckboxChange = (event: CheckboxChangeEvent, item: T) => {
    const key = item[itemKey as keyof T];
    // insert|update new item into the map and its checked status
    if (!!key) {
      itemMap.set(String(key), { item: item, checked: event.target.checked });
      setCheckedMap((prev) => {
        const newCheckMap = { ...prev };
        newCheckMap[String(key)] = event.target.checked;
        return newCheckMap;
      });
    }
  };

  useEffect(() => {
    const getSelectedItems = (itemMap: ItemMap) => {
      const itemMapKeys = Array.from(itemMap.keys());
      const checkedItemKeys = itemMapKeys.filter((key) => !!itemMap.get(key)?.checked);
      return checkedItemKeys.map((key) => itemMap.get(key)!.item);
    };
    setSelectedItems(getSelectedItems(itemMap));

    // itemMap as dependency won't work since React can't detect changes from Map object => use the checkedMap as a bridge to enable change detection
  }, [checkedMap, itemMap]);

  const clearAllCheckboxes = () => {
    itemMap.clear();
    setSelectedItems([]);
    setCheckedMap({});
  };

  return {
    onCheckboxChange,
    clearAllCheckboxes,
    selectedItems,
    checkedMap,
  };
};

export default {
  useAppMenu,
  useWindowDimensions,
  useSellerFetch,
  useIsMountedRef,
  useBranchData,
  useQuery,
  useItemCheckbox,
  useWithLoading,
};
